import { useCallback, useEffect, useMemo, useState } from "react";

import { CopyBlock, dracula } from "react-code-blocks";

import withErrorHandling from "../HOC/ErrorHandling";
import { useSharedProps } from "../HOC/SharedProps";
import { useBindingGraph } from "../HOC/BindingGraphProvider";

import {
  Visibility,
  VisibilityOff,
  Info as InfoIcon,
  Lock as LockIcon,
  Refresh as RefreshIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import {
  NEW_ITEM,
  DELETE_ITEM,
  JSON_ITEM,
  MAX_LIMITS,
} from "../helpers/constants";
import t from "../helpers/en";
import {
  getServiceCatalog,
  newServiceCatalog,
  getServiceCatalogByPath,
  onDeleteCatalogByPath,
} from "../helpers/utils";
import { hasPermission } from "../helpers/selectors";
import { useNavigate } from "react-router-dom";

import Loader from "../components/Loading";
import Modal from "../components/Modal/Modal";
import Select from "../components/Inputs/Select";
import ServiceInstanceCard, {
  InstanceActionsMenu,
  BoundAppsRow,
} from "../components/DaariDashboard/ServiceInstanceCard";

import { freeTierStyles } from "../GeneralStyles";

const DAARI_VIEW_STORAGE = "daari.viewMode";
const readSavedView = () => {
  try {
    const v = localStorage.getItem(DAARI_VIEW_STORAGE);
    return v === "list" ? "list" : "grid";
  } catch {
    return "grid";
  }
};

const POSTGRES_PATH = "postgres";

const ServiceCatalog = (props) => {
  const { currentUser, showError, showSuccess, categoryFilter, embedded } = props;

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [data, setData] = useState([]);
  const [form, setForm] = useState({});
  const [modalData, setModalData] = useState({ open: false });
  const [showPassword, setShowPassword] = useState(false);
  const [postgresInstances, setPostgresInstances] = useState(0);

  // path -> array of instance records (auto-populated for every catalog
  // type on mount so users see their services without clicking a chevron).
  const [instancesByPath, setInstancesByPath] = useState({});
  // path -> boolean, so per-type refresh spinners don't block the whole page.
  const [instancesLoadingByPath, setInstancesLoadingByPath] = useState({});
  const [viewMode, setViewMode] = useState(readSavedView);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Bindings come from the project-scoped composite index (coord PR #231)
  // via BindingGraphProvider — one fetch per project, O(1) lookups here.
  const {
    bindingsForInstance: graphBindingsForInstance,
    loading: bindingsLoading,
    invalidateAfterMutation: invalidateBindingGraph,
  } = useBindingGraph();

  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();

  // When mounted inside DaariView with a categoryFilter, only render the
  // catalog types that match the selected category.
  const displayData = useMemo(() => {
    if (!categoryFilter) return data;
    return (data || []).filter((t) => t?.category === categoryFilter);
  }, [data, categoryFilter]);

  const { currentGroup } = useSharedProps();

  const fetchServiceCatalog = () => {
    return new Promise((resolve, reject) => {
      getServiceCatalog(null, (result, err) => {
        if (Array.isArray(result)) {
          resolve(result);
        } else {
          reject(err);
        }
      });
    });
  };

  const fetchInstancesForPath = useCallback(
    (path) => {
      if (!path) return Promise.resolve([]);
      setInstancesLoadingByPath((prev) => ({ ...prev, [path]: true }));
      return new Promise((resolve) => {
        getServiceCatalogByPath({ path }, (result, err) => {
          setInstancesLoadingByPath((prev) => ({ ...prev, [path]: false }));
          if (!err) {
            const list = Array.isArray(result) ? result : [];
            setInstancesByPath((prev) => ({ ...prev, [path]: list }));
            if (path === POSTGRES_PATH) setPostgresInstances(list.length);
            resolve(list);
          } else {
            showError(err);
            resolve([]);
          }
        });
      });
    },
    [showError]
  );

  const fetchAllInstances = useCallback(
    (types) => {
      const paths = (types || [])
        .map((elem) => elem?.path)
        .filter((p) => typeof p === "string" && p.length > 0);
      if (paths.length === 0) return Promise.resolve();
      return Promise.all(paths.map((p) => fetchInstancesForPath(p)));
    },
    [fetchInstancesForPath]
  );

  useEffect(() => {
    setIsLoading(true);
    fetchServiceCatalog()
      .then((result) => {
        setData(result);
        const relevant = categoryFilter
          ? (result || []).filter((t) => t?.category === categoryFilter)
          : result || [];
        fetchAllInstances(relevant).finally(() => setIsLoading(false));
      })
      .catch((err) => {
        showError(err);
        setIsLoading(false);
      });
    // Auto-fetch runs whenever the project or category changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGroup, currentUser, categoryFilter]);

  const refreshAll = useCallback(() => {
    setIsRefreshing(true);
    Promise.all([
      fetchAllInstances(displayData || []),
      invalidateBindingGraph(),
    ]).finally(() => setIsRefreshing(false));
  }, [displayData, fetchAllInstances, invalidateBindingGraph]);

  const onCloseModal = () => {
    setModalData({ open: false });
    setForm({});
  };

  const onFieldChange = (fieldName, value) => {
    setForm((prevState) => ({ ...prevState, [fieldName]: value }));
  };

  const onSubmitForm = () => {
    if (modalData?.isSubmitting) return;

    const createdName = form?.name || "";
    const createdPath = modalData?.path;
    const typeLabel = modalData?.name || createdPath || "service";

    setModalData((prev) => ({ ...prev, isSubmitting: true }));

    newServiceCatalog({ data: form, path: createdPath }, (result, err) => {
      if (!err) {
        onCloseModal();
        showSuccess?.(
          createdName
            ? `Created ${typeLabel} '${createdName}' — provisioning…`
            : `Created new ${typeLabel} — provisioning…`
        );
        // Re-fetch just the type that changed so the new instance
        // appears in the grid immediately. Then refresh bindings for
        // that type only — a brand-new instance can't have bindings
        // yet, but the wizard template path can auto-bind, so we
        // need to see any resulting bindings on the new card.
        if (createdPath) {
          fetchInstancesForPath(createdPath).then(() => invalidateBindingGraph());
        }
      } else {
        setModalData((prev) => ({ ...prev, isSubmitting: false }));
        showError(err);
      }
    });
  };

  const onDelete = () => {
    if (modalData?.isSubmitting) return;

    const deletedPath = modalData?.path;
    const deletedName = modalData?.metadata?.displayName || modalData?.metadata?.name;

    setModalData((prev) => ({ ...prev, isSubmitting: true }));

    onDeleteCatalogByPath(
      { path: deletedPath, name: modalData?.metadata?.name },
      (result, err) => {
        if (!err) {
          onCloseModal();
          if (deletedName) {
            showSuccess?.(`Deleted '${deletedName}' — cleanup in progress…`);
          }
          if (deletedPath) {
            fetchInstancesForPath(deletedPath).then((list) =>
              invalidateBindingGraph()
            );
          }
        } else {
          setModalData((prev) => ({ ...prev, isSubmitting: false }));
          showError(err);
        }
      }
    );
  };

  // Open the create dialog for a specific catalog type.
  const openCreate = (elem) => {
    const initial = {};
    elem?.settings?.view?.forEach((field) => {
      initial[field.fieldName] =
        field.defaultValue === "t3.xlarge" ? "t3.micro" : field.defaultValue;
    });
    setForm(initial);
    setModalData({ open: true, targetId: NEW_ITEM, ...elem });
  };

  // Open the JSON viewer for an instance record.
  const openJson = (record) => {
    setModalData({ open: true, targetId: JSON_ITEM, json: record });
  };

  // Open the delete confirmation for an instance record on a given type path.
  const openDelete = (record, path) => {
    setModalData({ open: true, targetId: DELETE_ITEM, ...record, path });
  };

  const handleViewModeChange = (_evt, next) => {
    if (!next) return;
    setViewMode(next);
    try {
      localStorage.setItem(DAARI_VIEW_STORAGE, next);
    } catch {
      // localStorage unavailable — fine, view mode just won't persist.
    }
  };

  // O(1) lookup on the cached graph index from BindingGraphProvider.
  const boundAppsFor = (path, record) => {
    const name = record?.metadata?.name;
    if (!path || !name) return [];
    return graphBindingsForInstance(path, name);
  };

  // Deep-link a chip to the apps dashboard with the target app pre-selected.
  // ManageApps supports ?openApp=<uid> and ?openApp=<name>; we prefer the uid
  // when available and fall back to displayName-or-name for robustness.
  const handleOpenApp = (app) => {
    if (!app) return;
    const target = app.uid || app.name || app.displayName;
    if (!target) return;
    navigate(`/manage-apps?openApp=${encodeURIComponent(target)}`);
  };

  // Menu action: open the per-instance detail page (PR-9).
  const handleOpenDetail = (record, elemPath) => {
    if (!record?.metadata?.name || !elemPath) return;
    navigate(`/daari/${encodeURIComponent(elemPath)}/${encodeURIComponent(record.metadata.name)}`);
  };

  let title, content, saveLabel, onConfirmModalAction;

  switch (modalData?.targetId) {
    case JSON_ITEM:
      title = t.json;
      content = (
        <Box>
          <CopyBlock
            customStyle={{ height: "550px", width: "100%", overflow: "scroll" }}
            text={JSON.stringify(modalData?.json, null, 4)}
            language={"en"}
            showLineNumbers={true}
            theme={dracula}
          />
        </Box>
      );
      break;
    case NEW_ITEM:
      title = `Create New ${modalData.name}`;
      content = (
        <>
          {modalData?.settings?.view?.map((field, fieldIdx) => {
            switch (field.fieldType) {
              case "string":
                return (
                  <TextField
                    key={fieldIdx}
                    label={field.fieldName}
                    fullWidth
                    margin="normal"
                    value={form[field.fieldName] || ""}
                    onChange={(e) =>
                      onFieldChange(field.fieldName, e.target.value)
                    }
                    helperText={field.fieldHint}
                    required={field.required}
                    type={
                      field.fieldName.toLowerCase().includes("password") &&
                      !showPassword
                        ? "password"
                        : "text"
                    }
                    InputProps={{
                      endAdornment: field.fieldName
                        .toLowerCase()
                        .includes("password") && (
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      ),
                    }}
                  />
                );

              case "integer":
                return (
                  <TextField
                    key={fieldIdx}
                    label={field.fieldName}
                    fullWidth
                    margin="normal"
                    type="number"
                    value={form[field.fieldName] || ""}
                    onChange={(e) =>
                      onFieldChange(field.fieldName, e.target.value)
                    }
                    helperText={field.fieldHint}
                    required={field.required}
                    inputProps={{
                      min: field.minValue,
                      max: field.maxValue,
                    }}
                  />
                );

              case "SINGLE_SELECT":
                return (
                  <Select
                    key={fieldIdx}
                    options={field.values}
                    value={
                      field.values?.find(
                        (it) => it.key === form?.[field.fieldName]
                      ) || ""
                    }
                    onChange={(evt, val) =>
                      onFieldChange(field.fieldName, val.key)
                    }
                    label={field.fieldName}
                    item="value"
                    specificDetails={{
                      textFieldProps: {
                        helperText: field.fieldHint,
                        required: field.required,
                      },
                    }}
                  />
                );

              case "boolean":
                return (
                  <FormControlLabel
                    key={fieldIdx}
                    control={
                      <Checkbox
                        checked={Boolean(form[field.fieldName])}
                        onChange={(e) =>
                          onFieldChange(field.fieldName, e.target.checked)
                        }
                      />
                    }
                    label={field.fieldHint}
                  />
                );

              default:
                return null;
            }
          })}
        </>
      );
      onConfirmModalAction = onSubmitForm;
      saveLabel = t.create;
      break;
    case DELETE_ITEM:
      title = t.delete(
        [modalData?.path, `'${modalData?.metadata?.displayName}'`].join(": ")
      );
      onConfirmModalAction = onDelete;
      saveLabel = t.confirm;
      break;
    default:
  }

  if (isLoading) {
    return <Loader />;
  }

  const limitExceededPostgres = !currentUser?.account?.billingEnabled && postgresInstances >= MAX_LIMITS.postgresInstances;
  const limitExceeded = limitExceededPostgres; // || other limits here;

  return (
    <>
      <Modal
        open={modalData?.open}
        handleClose={onCloseModal}
        closeLabel={t.cancel}
        onSave={onConfirmModalAction}
        specificDetails={{ isSubmitting: modalData?.isSubmitting }}
        {...{ saveLabel, title, content }}
      />

      {/* Page header — hidden when embedded inside DaariView, which
          provides its own header. */}
      {!embedded && (
        <>
          <h2
            style={{
              fontWeight: 700,
              fontSize: "1.75rem",
              marginBottom: "1rem",
              color: isDarkMode ? theme.palette.common.white : "#1C252E",
              textAlign: "center",
            }}
          >
            Service Type Catalog
          </h2>
          <p
            style={{
              fontSize: "1rem",
              color: isDarkMode ? theme.palette.grey[500] : "#919EAB",
              marginBottom: "2rem",
              textAlign: "center",
            }}
          >
            Explore the available service types such as Database, Storages, Queues etc.
          </p>
        </>
      )}

      {/* free tier */}
      {limitExceeded && (
        <sub style={freeTierStyles}>
          <InfoIcon /> {t.freeTier}
        </sub>
      )}

      <Box
        sx={{
          display: "grid",
          gap: (theme) => `calc(3 * ${theme.spacing(1)})`,
          gridTemplateColumns: {
            xs: "repeat(1, 1fr)",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
          },
        }}
      >
        {displayData?.map((elem, elemIdx) => (
          <Box
            key={elemIdx}
            sx={{
              backgroundColor: isDarkMode ? theme.palette.grey[800] : "#FFFFFF",
              color: isDarkMode ? theme.palette.common.white : "#1C252E",
              position: "relative",
              boxShadow: isDarkMode
                ? "0 0 2px 0 rgba(0 0 0 / 0.4), 0 12px 24px -4px rgba(0 0 0 / 0.24)"
                : "0 0 2px 0 rgba(145 158 171 / 0.2),0 12px 24px -4px rgba(145 158 171 / 0.12)",
              zIndex: 0,
              transition: "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1)",
              overflow: "hidden",
              borderRadius: "16px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              height: "100%",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gap: "1rem",
                padding: "1rem",
                flexGrow: 1,
              }}
            >
              <h5
                style={{ fontWeight: 600, fontSize: "1rem", lineHeight: 1.5 }}
              >
                {elem.name}
              </h5>
              <p
                style={{
                  fontWeight: 400,
                  fontSize: "0.75rem",
                  lineHeight: 1.5,
                  color: isDarkMode ? theme.palette.grey[500] : "#919EAB",
                }}
              >
                {elem.description}
              </p>
            </Box>
            {hasPermission(currentUser, [
              "SYSTEM",
              "ACCOUNT_ADMIN",
              "CreateServiceInstance",
            ]) && (
              <Button
                id={NEW_ITEM}
                onClick={() => openCreate(elem)}
                variant="contained"
                sx={{
                  textTransform: "none",
                  alignSelf: "flex-start",
                  margin: "1rem",
                }}
                disabled={
                  (elem.path === POSTGRES_PATH && limitExceededPostgres)
                }
                startIcon={
                  (elem.path === POSTGRES_PATH && limitExceededPostgres) ? (
                    <LockIcon />
                  ) : null
                }
              >
                {t.create}
              </Button>
            )}
          </Box>
        ))}
      </Box>


      {/* Your services — new dashboard-style toolbar + card/list grid */}
      <Box
        sx={{
          mt: 4,
          display: "flex",
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
          gap: 1.5,
          mb: 2,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: isDarkMode ? "#f1f5f9" : "#0f172a",
            }}
          >
            Your services
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: isDarkMode ? theme.palette.grey[400] : "#64748b",
              fontSize: "0.85rem",
            }}
          >
            {(() => {
              const total = (displayData || []).reduce(
                (acc, t) => acc + (instancesByPath[t.path]?.length || 0),
                0
              );
              return total === 0
                ? "No services yet — create one from a type above."
                : `${total} service${total === 1 ? "" : "s"} in this project.`;
            })()}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            aria-label="Daari view mode"
            sx={{
              background: isDarkMode ? "#1e293b" : "#f1f5f9",
              borderRadius: "10px",
              p: "4px",
              "& .MuiToggleButtonGroup-grouped": {
                border: "none",
                borderRadius: "8px !important",
                mx: 0.25,
                "&:not(:first-of-type), &:first-of-type": {
                  borderRadius: "8px !important",
                },
              },
              "& .MuiToggleButton-root": {
                px: 1.25,
                py: 0.5,
                textTransform: "none",
                fontWeight: 500,
                fontSize: "0.8rem",
                color: isDarkMode ? "#94a3b8" : "#64748b",
                gap: 0.5,
                "&.Mui-selected": {
                  background: "#F97316",
                  color: "#fff",
                  "&:hover": { background: "#EA580C" },
                },
              },
            }}
          >
            <ToggleButton value="grid" aria-label="Grid view">
              <GridViewIcon sx={{ fontSize: "1.05rem" }} />
              Grid
            </ToggleButton>
            <ToggleButton value="list" aria-label="List view">
              <ViewListIcon sx={{ fontSize: "1.05rem" }} />
              List
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={refreshAll}
            disabled={isRefreshing}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "10px",
              height: 36,
              px: 2,
              borderColor: isDarkMode ? theme.palette.grey[700] : "#e2e8f0",
              color: isDarkMode ? theme.palette.grey[300] : "#475569",
              "&:hover": {
                borderColor: isDarkMode
                  ? "rgba(253,186,116,0.35)"
                  : "rgba(234,88,12,0.25)",
                background: isDarkMode
                  ? "rgba(234,88,12,0.06)"
                  : "rgba(234,88,12,0.04)",
                color: isDarkMode ? "#fdba74" : "#9a3412",
              },
            }}
          >
            {isRefreshing ? "Refreshing…" : "Refresh"}
          </Button>
        </Box>
      </Box>

      {(displayData || []).map((elem, elemIdx) => {
        const records = instancesByPath[elem.path] || [];
        const isTypeLoading = Boolean(instancesLoadingByPath[elem.path]);
        const count = records.length;
        const canDelete = Boolean(
          currentGroup?.id && currentGroup?.isContributor2Group
        ) || hasPermission(currentUser, ["SYSTEM", "ACCOUNT_ADMIN"]);

        return (
          <Box key={elemIdx} sx={{ mt: 3 }}>
            {/* Per-type header row */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1.25,
                pb: 0.75,
                borderBottom: `1px solid ${
                  isDarkMode ? theme.palette.grey[800] : "#e2e8f0"
                }`,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  color: isDarkMode ? "#e2e8f0" : "#0f172a",
                }}
              >
                {elem.name}
              </Typography>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 24,
                  height: 20,
                  px: 0.75,
                  borderRadius: "999px",
                  background: isDarkMode
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(15,23,42,0.06)",
                  color: isDarkMode ? "#cbd5e1" : "#475569",
                  fontSize: "0.7rem",
                  fontWeight: 600,
                }}
              >
                {count}
              </Box>
              <Box sx={{ flex: 1 }} />
              <Tooltip title={`Refresh ${elem.name}`} arrow>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => fetchInstancesForPath(elem.path)}
                    disabled={isTypeLoading}
                  >
                    <RefreshIcon
                      fontSize="small"
                      sx={{
                        color: isDarkMode ? "#94a3b8" : "#64748b",
                        animation: isTypeLoading ? "spin 1s linear infinite" : "none",
                        "@keyframes spin": {
                          from: { transform: "rotate(0deg)" },
                          to: { transform: "rotate(360deg)" },
                        },
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            {count === 0 ? (
              <Typography
                variant="body2"
                sx={{
                  color: isDarkMode ? theme.palette.grey[500] : "#94a3b8",
                  fontStyle: "italic",
                  fontSize: "0.85rem",
                  py: 1.5,
                }}
              >
                {isTypeLoading ? "Loading…" : `No ${elem.name.toLowerCase()} yet.`}
              </Typography>
            ) : viewMode === "grid" ? (
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: {
                    xs: "repeat(1, 1fr)",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                    lg: "repeat(4, 1fr)",
                  },
                }}
              >
                {records.map((record, recordIdx) => (
                  <ServiceInstanceCard
                    key={record?.metadata?.name || recordIdx}
                    record={record}
                    path={elem.path}
                    isDarkMode={isDarkMode}
                    onViewJson={openJson}
                    onDelete={(rec) => openDelete(rec, elem.path)}
                    onOpenApp={handleOpenApp}
                    onOpenDetail={handleOpenDetail}
                    canDelete={canDelete}
                    boundApps={boundAppsFor(elem.path, record)}
                    boundAppsLoading={bindingsLoading}
                  />
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  border: `1px solid ${
                    isDarkMode ? theme.palette.grey[800] : "#e2e8f0"
                  }`,
                  borderRadius: "12px",
                  overflow: "hidden",
                  background: isDarkMode ? theme.palette.grey[900] : "#fff",
                }}
              >
                {records.map((record, recordIdx) => {
                  const displayName =
                    record?.metadata?.displayName ||
                    record?.metadata?.name ||
                    "(unnamed)";
                  const created = record?.metadata?.creationTimestamp
                    ? new Date(
                        record.metadata.creationTimestamp
                      ).toLocaleString("en-US")
                    : "";
                  const statusText =
                    record?.status?.state ||
                    record?.status?.tableStatus ||
                    record?.status?.conditions?.[0]?.status ||
                    "-";
                  const isDefault = Boolean(record?.metadata?.labels?.default);
                  return (
                    <Box
                      key={record?.metadata?.name || recordIdx}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1.5fr 1fr 1fr 44px",
                        alignItems: "center",
                        gap: 1.5,
                        px: 2,
                        py: 1.25,
                        borderTop:
                          recordIdx === 0
                            ? "none"
                            : `1px solid ${
                                isDarkMode ? theme.palette.grey[800] : "#f1f5f9"
                              }`,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          minWidth: 0,
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: isDarkMode ? "#e2e8f0" : "#0f172a",
                              wordBreak: "break-word",
                            }}
                          >
                            {displayName}
                          </Typography>
                          {isDefault && (
                            <Box
                              component="span"
                              sx={{
                                px: 0.75,
                                py: 0.25,
                                borderRadius: "999px",
                                fontSize: "0.65rem",
                                fontWeight: 600,
                                background: isDarkMode
                                  ? "rgba(234,88,12,0.15)"
                                  : "rgba(234,88,12,0.08)",
                                color: isDarkMode ? "#fdba74" : "#9a3412",
                              }}
                            >
                              default
                            </Box>
                          )}
                        </Box>
                        <BoundAppsRow
                          boundApps={boundAppsFor(elem.path, record)}
                          loading={bindingsLoading}
                          isDarkMode={isDarkMode}
                          onOpenApp={handleOpenApp}
                          maxVisible={3}
                          compact
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: isDarkMode ? "#94a3b8" : "#64748b",
                          fontSize: "0.8rem",
                          textTransform: "capitalize",
                        }}
                      >
                        {String(statusText).toLowerCase()}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: isDarkMode ? "#64748b" : "#94a3b8",
                          fontSize: "0.8rem",
                        }}
                      >
                        {created}
                      </Typography>
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <InstanceActionsMenu
                          record={record}
                          path={elem.path}
                          isDarkMode={isDarkMode}
                          canDelete={canDelete && !isDefault}
                          onViewJson={openJson}
                          onDelete={(rec) => openDelete(rec, elem.path)}
                          onOpenDetail={handleOpenDetail}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        );
      })}
    </>
  );
};

export default withErrorHandling(ServiceCatalog);
